import React, { FC, ReactElement, useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Close, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useDropzone, FileRejection } from "react-dropzone";

import { IconImage, ModalV2 } from "@/components";
import { pdf_file_img } from "@/constants";
import {
  useCreateCustomsBatchMutation,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { formatFileSize } from "@/utils/helpers";
import {
  DropzoneContainer,
  DropzoneSmallText,
  IconContainer,
} from "./styles";

// Allowed MIME types and their labels
const ALLOWED_TYPES: Record<string, { label: string; maxBytes: number }> = {
  "application/pdf": { label: "PDF", maxBytes: 20 * 1024 * 1024 },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    label: "Word",
    maxBytes: 10 * 1024 * 1024,
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    label: "Excel",
    maxBytes: 10 * 1024 * 1024,
  },
  "image/png": { label: "PNG", maxBytes: 10 * 1024 * 1024 },
  "image/jpeg": { label: "JPEG", maxBytes: 10 * 1024 * 1024 },
  "image/tiff": { label: "TIFF", maxBytes: 20 * 1024 * 1024 },
};

interface FileUpload {
  id: string;
  file: File;
  status: "pending" | "uploading" | "uploaded" | "error";
  progress: number;
  error?: string;
  docId?: string;
}

interface UploadCustomsDocumentsProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onUploadComplete?: () => void;
  tenantId?: string;
  clientId?: string;
}

const UploadCustomsDocuments: FC<UploadCustomsDocumentsProps> = ({
  show,
  setShow,
  onUploadComplete,
  tenantId,
  clientId,
}): ReactElement => {
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const xhrRefs = useRef<XMLHttpRequest[]>([]);

  const { show: toast } = useSnackbar();
  const [createBatch] = useCreateCustomsBatchMutation();

  // Reset state and abort in-flight uploads when modal closes or unmounts
  useEffect(() => {
    if (!show) {
      // Abort all in-flight XHR uploads
      xhrRefs.current.forEach((xhr) => xhr.abort());
      xhrRefs.current = [];
      setUploads([]);
      setIsUploading(false);
    }
    
    // Cleanup on unmount
    return () => {
      xhrRefs.current.forEach((xhr) => xhr.abort());
      xhrRefs.current = [];
    };
  }, [show]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        toast("Some files were rejected. Check file type and size.", "", "error");
      }

      const newUploads: FileUpload[] = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: "pending" as const,
        progress: 0,
      }));

      setUploads((prev) => [...prev, ...newUploads]);
    },
    [toast]
  );

  const validator = (file: File) => {
    const typeSettings = ALLOWED_TYPES[file.type];
    if (!typeSettings) {
      return {
        code: "file-invalid-type",
        message: "File type not allowed",
      };
    }
    if (file.size > typeSettings.maxBytes) {
      return {
        code: "file-too-large",
        message: `File is larger than ${formatFileSize(typeSettings.maxBytes)}`,
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    validator,
    accept: Object.keys(ALLOWED_TYPES).reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {} as Record<string, string[]>),
    multiple: true,
    disabled: isUploading,
  });

  const removeFile = (index: number) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToS3 = async (
    file: File,
    uploadUrl: string,
    onProgress: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRefs.current.push(xhr);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener("load", () => {
        // Remove from refs when done
        xhrRefs.current = xhrRefs.current.filter((x) => x !== xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new globalThis.Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        // Remove from refs when done
        xhrRefs.current = xhrRefs.current.filter((x) => x !== xhr);
        reject(new globalThis.Error("Upload failed"));
      });

      xhr.addEventListener("abort", () => {
        // Remove from refs when aborted
        xhrRefs.current = xhrRefs.current.filter((x) => x !== xhr);
        reject(new globalThis.Error("Upload cancelled"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  };

  const handleUpload = async () => {
    if (uploads.length === 0) {
      toast("Please add files to upload", "", "error");
      return;
    }

    setIsUploading(true);

    try {
      // Create batch with all files to get pre-signed URLs
      const files = uploads.map((u) => ({
        filename: u.file.name,
        mimeType: u.file.type,
        sizeBytes: u.file.size,
      }));

      const batchResponse = await createBatch({
        files,
        tenantId,
        clientId,
      }).unwrap();

      // Upload each file to S3 using the pre-signed URLs
      // Map by index since batchResponse.documents preserves order of input files
      const uploadPromises = batchResponse.documents.map(async (doc, index) => {
        const upload = uploads[index];
        if (!upload) {
          console.error(`No matching upload found for index ${index}`);
          return;
        }

        setUploads((prev) =>
          prev.map((u) =>
            u.id === upload.id
              ? { ...u, status: "uploading" as const, docId: doc.docId }
              : u
          )
        );

        try {
          await uploadToS3(upload.file, doc.uploadUrl, (progress) => {
            setUploads((prev) =>
              prev.map((u) =>
                u.id === upload.id ? { ...u, progress } : u
              )
            );
          });

          setUploads((prev) =>
            prev.map((u) =>
              u.id === upload.id
                ? { ...u, status: "uploaded" as const, progress: 100 }
                : u
            )
          );
        } catch (err) {
          setUploads((prev) =>
            prev.map((u) =>
              u.id === upload.id
                ? {
                    ...u,
                    status: "error" as const,
                    error: (err as globalThis.Error).message,
                  }
                : u
            )
          );
        }
      });

      await Promise.all(uploadPromises);

      toast(
        "Upload Complete",
        `Uploaded ${uploads.length} file${uploads.length !== 1 ? "s" : ""}. Processing will begin automatically - you can track progress on the Document Storage page.`,
        "success"
      );

      onUploadComplete?.();
      setShow(false);
    } catch (err) {
      toast("Upload Failed", (err as globalThis.Error).message, "error");
      setIsUploading(false);
    }
  };

  const getFileIcon = () => {
    return pdf_file_img;
  };

  const pendingCount = uploads.filter((u) => u.status === "pending").length;

  return (
    <ModalV2
      setShow={setShow}
      show={show}
      title="Upload Customs Documents"
      subTitle="Upload entry packages, invoices, and supporting documents. Files will be automatically processed and clustered."
      buttonText={
        isUploading
          ? "Uploading..."
          : `Upload ${uploads.length} File${uploads.length !== 1 ? "s" : ""}`
      }
      buttonAction={handleUpload}
      isConfirmButtonLoading={isUploading}
      buttonLoader={uploads.length === 0}
    >
      <Box>
        <Typography
          sx={{
            marginTop: "10px",
            marginBottom: "8px",
            color: "#030712",
          }}
          variant="body2"
        >
          Upload Documents (PDF, Word, Excel, Images)
        </Typography>

        <DropzoneContainer
          {...getRootProps()}
          className={isDragActive ? "active" : ""}
          sx={{ opacity: isUploading ? 0.6 : 1 }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography color="#0B494B" variant="body1">
              Drop the files here ...
            </Typography>
          ) : (
            <>
              <IconContainer>
                <Box
                  component="img"
                  src="/upload-icon.svg"
                  sx={{ width: 40, height: 40 }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </IconContainer>
              <Typography color="#000" marginBottom="5px" variant="body1">
                Drag & drop files or{" "}
                <span
                  style={{
                    color: "#0B494B",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                  }}
                >
                  browse files
                </span>
              </Typography>
              <DropzoneSmallText>
                PDF, Word, Excel, PNG, JPEG, TIFF - Max 20MB per file
              </DropzoneSmallText>
            </>
          )}
        </DropzoneContainer>

        {/* File List */}
        {uploads.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: "#6B7280" }}>
              {uploads.length} file{uploads.length !== 1 ? "s" : ""} selected
              {pendingCount > 0 && ` (${pendingCount} pending)`}
            </Typography>

            {uploads.map((upload, index) => (
              <Box
                key={upload.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  mb: 1,
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  bgcolor: upload.status === "error" ? "#FEF2F2" : "#FAFAFA",
                }}
              >
                <IconImage src={getFileIcon()} size="32px" />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {upload.file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    {formatFileSize(upload.file.size)}
                  </Typography>

                  {upload.status === "uploading" && (
                    <LinearProgress
                      variant="determinate"
                      value={upload.progress}
                      sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                    />
                  )}

                  {upload.error && (
                    <Typography variant="caption" sx={{ color: "#DC2626" }}>
                      {upload.error}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {upload.status === "uploaded" && (
                    <CheckCircle sx={{ color: "#10B981", fontSize: 20 }} />
                  )}
                  {upload.status === "error" && (
                    <ErrorIcon sx={{ color: "#DC2626", fontSize: 20 }} />
                  )}
                  {upload.status === "uploading" && (
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {upload.progress}%
                    </Typography>
                  )}

                  {!isUploading && (
                    <IconButton
                      size="small"
                      onClick={() => removeFile(index)}
                      sx={{ p: 0.5 }}
                    >
                      <Close sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ModalV2>
  );
};

export default UploadCustomsDocuments;